import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useRouter } from 'next/router';
import { getAsString } from 'helpers';
import { getUserTypeColor } from 'utils/colors';
import OpenApiConfiguration from 'api/OpenApiConfiguration';
import {
  useAcceptBookingModificationMutation,
  useDeclineBookingModificationMutation,
} from 'components/booking/mutationHooks';
import { atom, useAtom } from 'jotai';
import { useBookingTrip as useBookingTripBaseQuery } from 'components/booking/queryHooks';
import { Booking, DomesticV2BookingsBookingIdSharePostRequest } from 'swagger/models';
import { AxiosError } from 'axios';
import { BookingsApi } from 'swagger/apis/bookings-api';
import { CicoApi } from 'swagger/apis/cico-api';
import { useUserDetails } from 'auth/mutations/userHooks';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import { ButtonVariant } from 'components/ui/Button/Button';
import { GuestBookingsApi } from 'swagger/apis/guest-bookings-api';
import { ShareBookingsApi } from 'swagger/apis/share-bookings-api';
import { useCallback, useState } from 'react';
import { useAuthQuery } from '../../utils/queryHooks';

const bookingsApi = new BookingsApi(OpenApiConfiguration);
const cicoApi = new CicoApi(OpenApiConfiguration);
const guestBookingsApi = new GuestBookingsApi(OpenApiConfiguration);
const shareBookingsApi = new ShareBookingsApi(OpenApiConfiguration);

const isSharedViewAtom = atom(false);

export const useIsSharedView = () => {
  const { query } = useRouter();
  const [isSharedView, setIsSharedView] = useAtom(isSharedViewAtom);

  return { isSharedView, setIsSharedView, shortCode: getAsString(query.short_code) };
};

export const useBookingAcceptGuestInviteMutation = () =>
  useMutation(({ bookingId, body }: { bookingId: string; body: DomesticV2BookingsBookingIdSharePostRequest }) =>
    shareBookingsApi.domesticV2BookingsBookingIdSharePut(bookingId, body as unknown)
  );

export const useCreateBookingGuestInviteMutation = () =>
  useMutation(({ bookingId, body = {} }: { bookingId: string; body?: DomesticV2BookingsBookingIdSharePostRequest }) =>
    // @ts-ignore
    shareBookingsApi.domesticV2BookingsBookingIdSharePost(bookingId, body)
  );

export const useRemoveBookingGuestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ bookingId, guestPublicId }: { bookingId: string; guestPublicId: string }) =>
      guestBookingsApi.domesticV2BookingsBookingIdGuestsGuestPublicIdDelete(bookingId, guestPublicId),
    {
      onSuccess: async (_, params) => {
        await queryClient.invalidateQueries(['guestsBooking', params.bookingId]);
      },
    }
  );
};

export const useBookingGuestsQuery = (customBookingId?: string) => {
  const router = useRouter();
  const bookingId = customBookingId ?? getAsString(router.query.bookingId);
  const { data: { data: guests } = {}, ...rest } = useQuery(
    ['guestsBooking', bookingId],
    () => guestBookingsApi.domesticV2BookingsBookingIdGuestsGet(bookingId),
    {
      retry: 1,
    }
  );

  return {
    guests,
    ...rest,
  };
};

export const useCicoDocumentCheckIn = (bookingId: string) => {
  const { data: { data: cicoDocumentIn } = {}, ...rest } = useQuery(
    ['cicoDocumentCheckIn'],
    () => cicoApi.domesticV2BookingsIdCicoCheckInGet(bookingId),
    {
      retry: 1,
    }
  );

  return {
    cicoDocumentIn,
    ...rest,
  };
};
export const useCicoDocumentCheckOut = (bookingId: string) => {
  const { data: { data: cicoDocumentOut } = {}, ...rest } = useQuery(
    ['cicoDocumentCheckOut'],
    () => cicoApi.domesticV2BookingsIdCicoCheckOutGet(bookingId),
    {
      retry: 1,
    }
  );

  return {
    cicoDocumentOut,
    ...rest,
  };
};

export const useBookingTrip = (customBookingId?) => {
  const router = useRouter();
  const { isSharedView, shortCode } = useIsSharedView();
  const bookingId = customBookingId ?? getAsString(router.query.bookingId);

  const { bookingTrip, ...other } = useBookingTripBaseQuery(
    {
      enabled: !isSharedView && !!bookingId,
    },
    bookingId
  );

  const { data: { data: sharedBookingTrip = {} } = {}, ...otherFromSharedView } = useQuery(
    ['bookingTripSharedView', bookingId],
    () => bookingsApi.domesticV2BookingsIdSharedDetailsGet(bookingId, shortCode),
    {
      enabled: isSharedView && !!bookingId,
    }
  );

  if (isSharedView) {
    return {
      bookingTrip: sharedBookingTrip,
      bookingId,
      ...otherFromSharedView,
    };
  }

  return { bookingTrip, bookingId, ...other };
};

export const useUserType = (customBookingId?) => {
  const router = useRouter();
  const bookingId = customBookingId ?? getAsString(router.query.bookingId);

  const { isSharedView } = useIsSharedView();

  const { bookingTrip = {}, isLoading } = useBookingTrip(bookingId);
  const { userDetails, userId } = useUserDetails();

  if (isSharedView) {
    return {
      isRenter: true,
      variant: 'renter' as ButtonVariant,
      variantColor: getUserTypeColor('renter'),
      isSharedView: true,
    };
  }

  const isOwner = bookingTrip.owner?.id === userId;
  const isManager = (bookingTrip as Booking).boat?.boat_manager?.id === userId;
  const isOwnerOrManager = isOwner || isManager;
  const isRenter = bookingTrip.renter?.id === userId;
  const isCaptain = bookingTrip.captain?.id === userId && !isOwner;
  const isOwnerAndCaptain = bookingTrip.captain?.id === userId && isOwner;
  const isAdmin = userDetails.is_admin;

  const getButtonVariation = (): ButtonVariant => {
    if (isOwnerOrManager) return 'owner';
    if (isCaptain) return 'captain';
    return 'renter';
  };

  const buttonVariant = getButtonVariation();

  return {
    isLoading,
    isAdmin,
    isRenter: isRenter || (isAdmin && !isOwnerOrManager),
    isOwner,
    isManager,
    isOwnerOrManager,
    isOwnerAndCaptain,
    isCaptain,
    variant: buttonVariant,
    variantColor: getUserTypeColor(buttonVariant),
    userId,
  };
};

export const useAcceptModification = (onSuccessCallback?: () => void) => {
  const { enqueueSnackbar } = useLazySnackbar();
  const queryClient = useQueryClient();

  return useAcceptBookingModificationMutation({
    onSuccess: () => {
      queryClient.invalidateQueries('bookingModifications');
      queryClient.invalidateQueries('bookingTrip');
      onSuccessCallback?.();
    },
    onError: (e: AxiosError<any, any>) => {
      enqueueSnackbar(e.response.data.error_message, { variant: 'error', autoHideDuration: 3000 });
    },
  });
};

export const useDeclineModification = (onSuccessCallback?: () => void) => {
  const { enqueueSnackbar } = useLazySnackbar();
  const queryClient = useQueryClient();

  return useDeclineBookingModificationMutation({
    onSuccess: () => {
      onSuccessCallback?.();
      queryClient.invalidateQueries('bookingModifications');
      queryClient.invalidateQueries('bookingTrip');
    },
    onError: (e: AxiosError<any, any>) => {
      enqueueSnackbar(e.response.data.error_message, { variant: 'error', autoHideDuration: 3000 });
    },
  });
};

export const useBookingPricing = (customBookingId?) => {
  const router = useRouter();
  const bookingId = customBookingId ?? getAsString(router.query.bookingId);

  const { data: { data: bookingPricing = {} } = {}, ...other } = useAuthQuery(
    ['bookingPricing', bookingId],
    () => bookingsApi.domesticV2BookingsIdPricingGet(bookingId),
    {
      enabled: !!bookingId,
    }
  );
  return { bookingPricing, ...other };
};

export const useBookingFuelReimbursement = (customBookingId?) => {
  const router = useRouter();
  const bookingId = customBookingId ?? getAsString(router.query.bookingId);

  const { data: { data: fuelReimbursements = [] } = {}, ...other } = useAuthQuery(
    ['fuelReimbursements', bookingId],
    () => bookingsApi.domesticV2BookingsIdFuelReimbursementsGet(bookingId),
    {
      enabled: !!bookingId,
    }
  );
  return { fuelReimbursements, ...other };
};

export const useBookingModifications = (customBookingId?) => {
  const router = useRouter();
  const bookingId = customBookingId ?? getAsString(router.query.bookingId);
  const { isSharedView } = useIsSharedView();

  const { data: { data: bookingModifications = [] } = {}, ...other } = useAuthQuery(
    ['bookingModifications', bookingId],
    () => bookingsApi.domesticV2BookingsIdModificationsGet(bookingId),
    {
      enabled: !isSharedView && !!bookingId,
    }
  );

  const latestModification =
    bookingModifications.length > 0 && bookingModifications[0].state === 'submitted'
      ? bookingModifications[0]
      : undefined;

  return { bookingModifications, latestModification, ...other };
};

export const useGetShareBookingDetailsLink = () => {
  const [shareLink, setShareLink] = useState<string | null>(null);
  const { mutateAsync, isSuccess, isLoading } = useCreateBookingGuestInviteMutation();

  const generateShareBookingDetailsLink = useCallback(
    async (bookingId: string) => {
      const {
        data: { link },
      } = await mutateAsync({ bookingId });

      setShareLink(link);

      return link;
    },
    [mutateAsync]
  );

  return { generateShareBookingDetailsLink, shareBookingDetailsLink: shareLink, isLoading, isSuccess };
};
